import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import customerApi from "@/lib/customerApi";

const Ctx = createContext(null);

export function CustomerProvider({ children }) {
  const [customer, setCustomer] = useState(() => {
    try { return JSON.parse(localStorage.getItem("rtg_customer") || "null"); } catch { return null; }
  });
  const [loading, setLoading] = useState(false);

  const persist = useCallback((data) => {
    // v2.6.7 batch J — Google flow has NO access_token (cookie-based) but
    // DOES have a customer doc. Email/password still has both fields.
    if (data?.access_token) {
      localStorage.setItem("rtg_customer_token", data.access_token);
    }
    if (data?.customer) {
      localStorage.setItem("rtg_customer", JSON.stringify(data.customer));
      setCustomer(data.customer);
    }
  }, []);

  // v2.6.7 batch J — on first mount, if we're returning from a Google OAuth
  // redirect (URL fragment contains session_id=...) we DON'T do an /auth/me
  // probe — the AuthCallback page handles that exchange. Otherwise verify the
  // session cookie by calling /customer/me; if it succeeds, hydrate state.
  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return;
    if (customer) return; // already hydrated from localStorage
    let cancelled = false;
    customerApi.get("/customer/me")
      .then(r => { if (!cancelled && r?.data) { setCustomer(r.data); localStorage.setItem("rtg_customer", JSON.stringify(r.data)); } })
      .catch(() => {/* not logged in, that's fine */});
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (email, password, remember = false) => {
    setLoading(true);
    try { const { data } = await api.post("/customer/login", { email, password, remember: !!remember }); persist(data); return data; }
    finally { setLoading(false); }
  }, [persist]);
  const register = useCallback(async (payload) => {
    setLoading(true);
    try { const { data } = await api.post("/customer/register", payload); persist(data); return data; }
    finally { setLoading(false); }
  }, [persist]);
  const setFromGoogle = useCallback((custDoc) => {
    if (custDoc) {
      localStorage.setItem("rtg_customer", JSON.stringify(custDoc));
      setCustomer(custDoc);
    }
  }, []);
  // v2.7.65+E255 · Re-hydrate the current customer (used after profile edits +
  // saving/removing a Saved Place so the UI reflects changes immediately).
  const refresh = useCallback(async () => {
    try {
      const { data } = await customerApi.get("/customer/me");
      if (data) { setCustomer(data); localStorage.setItem("rtg_customer", JSON.stringify(data)); }
      return data;
    } catch { return null; }
  }, []);
  const logout = useCallback(async () => {
    try {
      await customerApi.post("/auth/google/logout");
    } catch (e) {
      // Server-side session may already be gone (e.g. cookie expired) — that's
      // fine, we still clear local state below.
      console.warn("Google logout endpoint failed (continuing client logout)", e?.response?.status || e?.message);
    }
    localStorage.removeItem("rtg_customer_token");
    localStorage.removeItem("rtg_customer");
    setCustomer(null);
  }, []);

  const value = useMemo(
    () => ({ customer, loading, login, register, setFromGoogle, logout, refresh }),
    [customer, loading, login, register, setFromGoogle, logout, refresh],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useCustomer = () => useContext(Ctx);
